import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addFoodToFavouriteRestaurent } from "../../redux/features/WishList/wishlistSlice";
import norestimage from "../../assets/images/norestimage.png";
import { filterExpiredCoupons } from "../../utils/couponUtils";

// Haversine formula to calculate distance in kilometers
const calculateDistanceInKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371; // Radius of Earth in KM

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Custom hook to get city for a restaurant
const useRestaurantCity = (restaurant) => {
  const [city, setCity] = useState(restaurant?.city || "Unknown Area");

  useEffect(() => {
    // Use city directly from restaurant data
    if (restaurant?.city) {
      setCity(restaurant.city);
    } else {
      setCity("Unknown Area");
    }
  }, [restaurant?.city]);

  return { city };
};

const RotatingOffers = ({ offers = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filter expired coupons
  const validOffers = useMemo(() => filterExpiredCoupons(offers), [offers]);

  useEffect(() => {
    if (!validOffers.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % validOffers.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [validOffers]);

  if (!validOffers.length) return null;

  const currentOffer = validOffers[currentIndex];

  return (
    <div className="swiggy-offer-badge" style={{
      position: 'absolute',
      bottom: '8px',
      left: '8px',
      width: '75%',
      maxWidth: '75%',
      color: 'white',
      padding: '4px',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '800',
      zIndex: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }}>
      <i className="fas fa-badge-percent" style={{ marginRight: '4px' }} />
      <span style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        maxWidth: 'calc(100% - 20px)'
      }}>
        {currentOffer.coupon_code}
      </span>
    </div>
  );
};

// Function to calculate dynamic delivery time
const calculateDeliveryTime = (restaurant, userLat, userLon) => {
  if (!restaurant?.latitude || !restaurant?.longitude || !userLat || !userLon) {
    return restaurant?.delivery_time || "30-35 mins";
  }

  const restLat = parseFloat(restaurant.latitude);
  const restLon = parseFloat(restaurant.longitude);
  
  if (isNaN(restLat) || isNaN(restLon)) {
    return restaurant?.delivery_time || "30-35 mins";
  }

  // Calculate distance
  const distance = calculateDistanceInKm(userLat, userLon, restLat, restLon);
  
  // Base preparation time from restaurant data (in minutes)
  const basePrepTime = parseInt(restaurant.min_food_preparation_time) || 15;
  
  // Estimated travel time (assuming average speed of 20 km/h for delivery)
  const travelTimeMinutes = Math.round(distance * 3); // 3 minutes per km
  
  // Total delivery time
  const totalTime = basePrepTime + travelTimeMinutes;
  
  // Create a range for more realistic display
  const minTime = Math.max(totalTime - 5, 15);
  const maxTime = totalTime + 5;
  
  return `${minTime}-${maxTime} mins`;
};

// Function to get category names from category IDs
const getCategoryNames = (categoryIds, categories) => {
  if (!categoryIds || !categories || !Array.isArray(categories)) {
    return '--';
  }
  
  // Split the comma-separated category IDs
  const ids = categoryIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
  
  // Map IDs to category titles
  const categoryNames = ids.map(id => {
    const category = categories.find(cat => cat.id === id);
    return category ? category.title : null;
  }).filter(name => name !== null);
  
  return categoryNames.length > 0 ? categoryNames.join(', ') : '--';
};

// Skeleton Loader Component
const RestaurantSkeleton = () => (
  <div className="swiggy-grid-item">
    <div className="swiggy-restaurant-card">
      <div className="swiggy-card-image-container">
        <div 
          className="skeleton-image" 
          style={{
            width: '100%',
            height: '200px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '8px',
            animation: 'shimmer 1.5s infinite'
          }}
        />
      </div>
      <div className="swiggy-card-content">
        <div 
          className="skeleton-text" 
          style={{
            height: '20px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '4px',
            marginBottom: '8px',
            animation: 'shimmer 1.5s infinite'
          }}
        />
        <div 
          className="skeleton-text" 
          style={{
            height: '16px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '4px',
            marginBottom: '8px',
            width: '60%',
            animation: 'shimmer 1.5s infinite'
          }}
        />
        <div 
          className="skeleton-text" 
          style={{
            height: '14px',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '4px',
            width: '80%',
            animation: 'shimmer 1.5s infinite'
          }}
        />
      </div>
    </div>
  </div>
);

// Restaurant Card Component with React.memo for performance
const RestaurantCard = React.memo(({ restaurant, restaurentist, handleAddFoodToWishlist, userLat, userLon, categories }) => {
  
  const { city } = useRestaurantCity(restaurant);
  const deliveryTime = calculateDeliveryTime(restaurant, userLat, userLon);
  const isOffline = restaurant?.status === "offline";
  

  return (
    <div className="swiggy-grid-item" key={restaurant.id}>
      <Link 
        to={isOffline ? "#" : `/restaurent-list/${restaurant?.id}`} 
        className="swiggy-card-link"
        onClick={isOffline ? (e) => e.preventDefault() : undefined}
      >
        <div className={`swiggy-restaurant-card ${isOffline ? 'offline' : ''}`}>
          <div className="swiggy-card-image-container">
            <img
              src={restaurant?.restaurant_image_single || norestimage}
              alt={restaurant?.restaurant_name || "Restaurant"}
              className="swiggy-card-image"
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                if (!isOffline) {
                  handleAddFoodToWishlist(restaurant);
                }
              }}
              className="swiggy-fav-btn"
              disabled={isOffline}
            >
              <i
                className={
                  restaurentist.some((item) => item.id === restaurant.id)
                    ? "fas fa-heart swiggy-heart-filled"
                    : "far fa-heart swiggy-heart-outline"
                }
              />
            </button>
            {restaurant?.offer && restaurant.offer.length > 0 && (
              <RotatingOffers offers={restaurant.offer} />
            )}
            {/* Pure Veg Indicator */}
            {restaurant?.is_veg === "0" && (
              <div className="swiggy-pure-veg-badge">
                <i className="fas fa-leaf" />
                Pure Veg
              </div>
            )}
            {/* Quick Delivery Indicator - show if delivery_time <= 30 OR if no veg indicator */}
            {((restaurant?.delivery_time && parseInt(restaurant.delivery_time) <= 30) || restaurant?.is_veg !== "0") && (
              <div className={`swiggy-quick-delivery-badge ${restaurant?.is_veg === "0" ? 'swiggy-quick-delivery-badge-with-veg' : ''}`}>
                <i className="fas fa-bolt" />
                Quick
              </div>
            )}
            {/* New Restaurant Badge */}
            {restaurant?.is_new && (
              <div className="swiggy-new-badge">
                <i className="fas fa-star" />
                New
              </div>
            )}
            {/* Offline Badge */}
            {isOffline && (
              <div className="swiggy-offline-badge">
                <i className="fas fa-power-off" />
                CURRENTLY CLOSED
              </div>
            )}
          </div>
          <div className="swiggy-card-content">
            {/* 1. Restaurant Name */}
            <h5 className="swiggy-restaurant-name" style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}>{restaurant?.restaurant_name}</h5>
            
            {/* 2. Rating with star icon in circle */}
            <div className="d-flex align-items-center gap-1">
              <span className="swiggy-rating-container swiggy-rating">
                <i className="fas fa-star" />
              </span>
              <span className="swiggy-delivery-time"> {restaurant?.rating?.toFixed(1)}</span>
              <i className="fas fa-circle" style={{fontSize: '4px'}} /> 
              <span className="swiggy-delivery-time">{deliveryTime}</span>
            </div>
            <div className="food-items-serving-container">
              <span className="swiggy-delivery-time" style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block'
              }}>
                {getCategoryNames(restaurant?.category_id, categories)}
              </span>
            </div>
            
            {/* 4. Location area name */}
            <div className="swiggy-location">
              <span className="swiggy-distance">
                <i className="fas fa-map-marker-alt" />
                {city}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
});

function RestaurantSlide({ restaurant = [], categories = [] }) {
  const dispatch = useDispatch();
  const restaurentist = useSelector((state) => state.wishlist.restaurentist);

  // Lazy loading state
  const [visibleCount, setVisibleCount] = useState(8); // Load 8 restaurants initially
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);

  const userLocation = JSON.parse(localStorage.getItem("userLocation") || '{"latitude":0,"longitude":0}');
  const userLat = parseFloat(userLocation.latitude);
  const userLon = parseFloat(userLocation.longitude);

  // Show only restaurants within delivery radius and sort by status - memoized for performance
  const filteredRestaurants = useMemo(() => {
    return restaurant
      .filter((rest) => {
        const restLat = parseFloat(rest.latitude);
        const restLon = parseFloat(rest.longitude);
        const deliveryRadius = parseFloat(rest.delivery_radius_in_km);

        if (isNaN(restLat) || isNaN(restLon) || isNaN(deliveryRadius)) return false;

        const distance = calculateDistanceInKm(userLat, userLon, restLat, restLon);

        return distance <= deliveryRadius;
      })
      .sort((a, b) => {
        // Sort by status: "online" first, then "offline"
        if (a.status === "online" && b.status === "offline") return -1;
        if (a.status === "offline" && b.status === "online") return 1;
        return 0; // Keep original order for same status
      });
  }, [restaurant, userLat, userLon]);

  // Get visible restaurants for current page
  const visibleRestaurants = useMemo(() => {
    return filteredRestaurants.slice(0, visibleCount);
  }, [filteredRestaurants, visibleCount]);

  // Check if there are more restaurants to load
  useEffect(() => {
    setHasMore(visibleCount < filteredRestaurants.length);
  }, [visibleCount, filteredRestaurants.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoading) {
          loadMoreRestaurants();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, isLoading]);

  // Load more restaurants function
  const loadMoreRestaurants = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    
    // Reduced loading delay for faster data loading
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + 8, filteredRestaurants.length));
      setIsLoading(false);
    }, 150);
  }, [isLoading, hasMore, filteredRestaurants.length]);

  const handleAddFoodToWishlist = useCallback(async (menu) => {
    try {
      dispatch(addFoodToFavouriteRestaurent(menu));
    } catch (error) {
    }
  }, [dispatch]);

  return (
    <>
      <div className="swiggy-restaurant-grid">
        {visibleRestaurants.length > 0 ? (
          visibleRestaurants.map((arr) => (
            <RestaurantCard
              key={arr.id}
              restaurant={arr}
              restaurentist={restaurentist}
              handleAddFoodToWishlist={handleAddFoodToWishlist}
              userLat={userLat}
              userLon={userLon}
              categories={categories}
            />
          ))
        ) : (
          <div className="swiggy-no-restaurants">
            <p className="text-center text-danger">No Restaurant Found</p>
          </div>
        )}
        
        {/* Loading indicator for infinite scroll */}
        {hasMore && (
          <div ref={loadingRef} className="loading-trigger" style={{ height: '20px', margin: '20px 0' }}>
            {isLoading && (
              <div className="d-flex justify-content-center align-items-center">
                <div className="spinner-border text-danger" role="status" style={{ width: '2rem', height: '2rem' }}>
                  <span className="visually-hidden">Loading more restaurants...</span>
                </div>
                <span className="ms-2 text-muted">Loading more restaurants...</span>
              </div>
            )}
          </div>
        )}
        
        {/* Show skeleton loading while loading */}
        {isLoading && (
          <div className="swiggy-restaurant-grid">
            {Array.from({ length: 8 }).map((_, index) => (
              <RestaurantSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        )}
        
      </div>
    </>
  );
}

export default RestaurantSlide;

